/**
 * The candidate seam's HTTP backing.
 *
 * Everything goes through the generated `candidateClient` from @bymyword/api-types, which reaches
 * every non-admin path with the candidate JWT as `Authorization: Bearer`. Two clients, because the
 * rail has two auth states (the same shape the admin rail uses):
 *   - `authApi`      — provider returns null. The `/api/auth/*` routes take no bearer, and this is
 *     also what rotation uses, so refreshing can never recurse into itself.
 *   - `candidateApi` — provider returns the live token, rotating it when it is about to expire.
 *     Called per request, so a rotation is picked up without rebuilding the client.
 *
 * The candidate auth + request responses are the Phase-1 carve-out: the generated schema types their
 * bodies as `content?: never`, so `.post`/`.get` resolve `unknown` and the results are cast to the
 * hand-authored wire types in `wire.ts` (each of which mirrors the backend record exactly). When the
 * carve-out is lifted the casts fall away.
 */
import { ApiError, candidateClient, type AccessTokenProvider } from '@bymyword/api-types';
import { useCandidateSession, signOut } from '@/auth/session';
import { getCaptchaToken } from '@/auth/captcha';
import type {
  CreateRequestResponse,
  LedgerItemResponse,
  ReferenceLetterResponse,
  ReferralInfoResponse,
  RequestDetailResponse,
  TokenResponse,
} from './wire';
import { toLedgerCard, toLedgerCards, type LedgerCard } from './ledger';
import { buildHttpContent, type ReferenceContent, type SignedLetterFields } from './letter-content';
import { CandidateApiError, type CandidateErrorKind } from './errors';
import type {
  CandidateApiClient,
  CheckoutSession,
  CreateRequestResult,
  NewRequestInput,
  PdfDownload,
  ReferralInfo,
  RequestDetail,
  UnlockOptions,
  WaitlistInput,
} from './api-client';

// Same-origin (blank VITE_API_BASE_URL, the documented prod default) must still resolve to an
// ABSOLUTE URL: the generated client builds requests with `new URL(...)`, and `new URL('/api/x')`
// with no base throws. Fall back to the page origin, exactly as referee-web / admin do.
const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '') ||
  (typeof window !== 'undefined' ? window.location.origin : '');

/** The token is rotated this long before it expires, so a request never races the clock. */
const ROTATE_BEFORE_EXPIRY_MS = 60_000;

/** The unauthenticated leg. Its provider returns null unconditionally to prevent refresh recursion. */
const authApi = candidateClient(BASE_URL, () => null);

let inFlightRotation: Promise<string | null> | null = null;

/**
 * Whether a FAILED token rotation should end the session. Only an AUTH-DEFINITIVE rejection does: the
 * refresh endpoint answering 401 (token spent/revoked) or 400 (malformed) means no retry can ever
 * succeed, so the session is genuinely over. A network/transport error (fetch threw) or a 5xx is a
 * blip, NOT proof the session is dead — signing out on those would evict a valid session over a
 * dropped packet, so we leave it intact and let the caller retry. Exported so this exact decision is
 * unit-tested without driving a live fetch.
 */
export function rotationFailureEndsSession(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 400);
}

function accessTokenIsFresh(expiresAt: string | null): boolean {
  if (expiresAt === null) return false;
  const expiry = new Date(expiresAt).getTime();
  if (Number.isNaN(expiry)) return false;
  return expiry - Date.now() > ROTATE_BEFORE_EXPIRY_MS;
}

/**
 * Rotate the pair. Deduped: several queries firing at once must produce ONE refresh, or they race
 * to spend the same single-use refresh token and the loser's replay revokes the whole family (the
 * server revokes a family on a replayed rotation — correct behaviour that would sign the candidate
 * out mid-action).
 */
function rotate(refreshToken: string): Promise<string | null> {
  inFlightRotation ??= authApi
    .post('/api/auth/refresh', { body: { refreshToken } })
    .then((res) => {
      const tokens = res as unknown as TokenResponse;
      useCandidateSession.getState().rotate(tokens);
      return tokens.accessToken;
    })
    .catch((error: unknown) => {
      // Sign out only on an auth-definitive rejection; leave the session intact on a network/5xx blip
      // and return null so the in-flight call surfaces a retryable error and the next action retries.
      if (rotationFailureEndsSession(error)) {
        signOut();
      }
      return null;
    })
    .finally(() => {
      inFlightRotation = null;
    });

  return inFlightRotation;
}

const tokenProvider: AccessTokenProvider = async () => {
  const { accessToken, accessTokenExpiresAt, refreshToken } = useCandidateSession.getState();
  if (accessToken !== null && accessTokenIsFresh(accessTokenExpiresAt)) return accessToken;
  if (refreshToken !== null) return rotate(refreshToken);
  return accessToken;
};

const candidateApi = candidateClient(BASE_URL, tokenProvider);

/** Parse the `Retry-After` response header (delta-seconds form the server always uses) to a number. */
function retryAfterFrom(response: Response): number | undefined {
  const header = response.headers.get('Retry-After');
  if (header === null) return undefined;
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

/** Map a transport error to the seam's own error, so screens branch on one shape either way. */
function toCandidateError(error: unknown): CandidateApiError {
  if (error instanceof ApiError) {
    const problem = error.problem as { error?: unknown } | undefined;
    const code = typeof problem?.error === 'string' ? problem.error : undefined;
    const kind: CandidateErrorKind =
      error.status === 401
        ? 'unauthorized'
        : error.status === 429
          ? 'rate_limited'
          : error.status === 409
            ? 'conflict'
            : error.status === 403
              ? 'forbidden'
              : error.status === 404
                ? 'not_found'
                : 'server';
    // The server sends Retry-After on every 429; lift it so rate-limit copy can name the wait.
    return new CandidateApiError(error.message, kind, error.status, code, retryAfterFrom(error.response));
  }
  if (error instanceof CandidateApiError) return error;
  // A thrown non-ApiError is a fetch/parse failure — the network, not a rejected request.
  return new CandidateApiError('Network request failed', 'network', 0);
}

/**
 * Map the `letter`-tier body to the signed-letter fields the content builder needs, or null when the
 * reference is unlocked but not signed yet (`status: 'pending'`, all fields null → the viewer shows a
 * "being finalised" note). The endpoint returns a bare 403 for a locked read, so this is only reached
 * when unlocked.
 */
function toSignedLetterFields(res: ReferenceLetterResponse): SignedLetterFields | null {
  if (res.status !== 'ready' || res.body === null || res.signerName === null) {
    return null;
  }
  return {
    body: res.body,
    signerName: res.signerName,
    signerCompany: res.signerCompany,
    signerRelationship: res.signerRelationship,
    verificationCode: res.verificationCode ?? '',
    signedAt: res.signedAt ?? '',
  };
}

export function createHttpClient(): CandidateApiClient {
  return {
    async requestOtp(email) {
      try {
        await authApi.post('/api/auth/otp/request', {
          body: { email: email.trim(), captchaToken: await getCaptchaToken() },
        });
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async verifyOtp(email, code) {
      try {
        const res = await authApi.post('/api/auth/otp/verify', {
          body: { email: email.trim(), code: code.trim() },
        });
        return res as unknown as TokenResponse;
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async getLedger(): Promise<LedgerCard[]> {
      try {
        const res = await candidateApi.get('/api/requests');
        return toLedgerCards(res as unknown as LedgerItemResponse[]);
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    // --- requests (WP-F8b): the wizard + detail timeline + resend/cancel, all real endpoints ---
    async createRequest(input: NewRequestInput): Promise<CreateRequestResult> {
      try {
        const res = await candidateApi.post('/api/requests', {
          body: {
            refereeName: input.refereeName.trim(),
            refereeEmail: input.refereeEmail.trim(),
            relationshipContext: input.relationshipContext?.trim() || null,
            companyName: input.companyName?.trim() || null,
            personalNote: input.personalNote?.trim() || null,
            // Sent as given, never defaulted to true: the value of the record is that it says what
            // the candidate actually claimed. A false reaches the server and is refused there.
            refereePermissionConfirmed: input.refereePermissionConfirmed,
          },
        });
        const created = res as unknown as CreateRequestResponse;
        return {
          requestId: created.requestId,
          status: created.status,
          euRefereeFlagged: created.euRefereeFlagged,
        };
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async getRequestDetail(id: string): Promise<RequestDetail> {
      try {
        const res = await candidateApi.get('/api/requests/{id}', { pathParams: { id } });
        const detail = res as unknown as RequestDetailResponse;
        const card = toLedgerCard(detail.request);
        // A detail whose status has no ledger card (cancelled / unrecognised) is not a viewable
        // request from the candidate's seat — fold it into the same not-found the mock returns.
        if (card === null) {
          throw new CandidateApiError('Request is not viewable', 'not_found', 404, 'not_found');
        }
        return { card, personalNote: detail.personalNote, timeline: detail.timeline };
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async resendRequest(id: string): Promise<void> {
      try {
        await candidateApi.post('/api/requests/{id}/resend', { pathParams: { id } });
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async cancelRequest(id: string): Promise<void> {
      try {
        await candidateApi.post('/api/requests/{id}/cancel', { pathParams: { id } });
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    // --- unlock / monetization (WP-F11a): real dual-path unlock, all typed via @bymyword/api-types ---
    async getUnlockOptions(id: string): Promise<UnlockOptions> {
      try {
        const res = await candidateApi.get('/api/references/{id}/unlock', { pathParams: { id } });
        return {
          alreadyUnlocked: res.alreadyUnlocked,
          freeCreditAvailable: res.freeCreditAvailable,
          referralBonusAvailable: res.referralBonusAvailable,
        };
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async unlockWithFreeCredit(id: string): Promise<void> {
      try {
        await candidateApi.post('/api/references/{id}/unlock/free', { pathParams: { id } });
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async unlockWithReferralBonus(id: string): Promise<void> {
      try {
        await candidateApi.post('/api/references/{id}/unlock/referral', { pathParams: { id } });
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async startUnlockCheckout(id: string): Promise<CheckoutSession> {
      try {
        const res = await candidateApi.post('/api/references/{id}/checkout', { pathParams: { id } });
        return { sessionId: res.sessionId, redirectUrl: res.redirectUrl };
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    // --- letter / unlock content (WP-B19 chokepoint): key points free, letter entitlement-gated ---
    // `alreadyUnlocked` comes from the unlock-options endpoint (the ledger wire still cannot carry an
    // entitlement — DESIGN-NOTES). Key points are always readable (the free teaser); the letter tier
    // is fetched only when unlocked, and its 403 for a locked read is never requested because the
    // options gate above already decided `unlocked:false`. The content tiers are now their own typed
    // routes (task-13), so `.get` resolves the generated bodies directly — no cast.
    async getReference(card: LedgerCard): Promise<ReferenceContent> {
      try {
        const [options, keyPointsRes] = await Promise.all([
          candidateApi.get('/api/references/{id}/unlock', { pathParams: { id: card.id } }),
          candidateApi.get('/api/references/{id}/content/keypoints', { pathParams: { id: card.id } }),
        ]);
        const keyPoints = keyPointsRes.keyPoints ?? [];
        const availability = {
          freeCreditAvailable: options.freeCreditAvailable,
          referralBonusAvailable: options.referralBonusAvailable,
        };

        if (!options.alreadyUnlocked) {
          return buildHttpContent({ card, keyPoints, unlocked: false, availability, letter: null });
        }

        const letterRes = await candidateApi.get('/api/references/{id}/content/letter', {
          pathParams: { id: card.id },
        });
        return buildHttpContent({
          card,
          keyPoints,
          unlocked: true,
          availability,
          letter: toSignedLetterFields(letterRes),
        });
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    // The reference's PDF (WP-F11a-follow-up): a short-TTL presigned R2 GET, gated by an active unlock.
    // `ready` carries the `downloadUrl` the viewer opens; `pending` means the post-sign render is still
    // in flight (never a 404 — the viewer offers "try again"); a locked read is a bare 403 → `forbidden`.
    async getLetterPdf(id: string): Promise<PdfDownload> {
      try {
        const res = await candidateApi.get('/api/references/{id}/content/pdf', { pathParams: { id } });
        return { status: res.status === 'ready' ? 'ready' : 'pending', downloadUrl: res.downloadUrl };
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    // --- WP-B18 endpoints ---
    async joinWaitlist(input: WaitlistInput) {
      try {
        await candidateApi.post('/api/waitlist', {
          body: {
            email: input.email.trim(),
            persona: input.persona,
            source: 'app_persona_picker',
            captchaToken: await getCaptchaToken(),
          },
        });
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async getReferral(): Promise<ReferralInfo> {
      try {
        const res = await candidateApi.get('/api/referrals/me');
        const info = res as unknown as ReferralInfoResponse;
        return { code: info.code, shareUrl: info.shareUrl, bonusEarned: info.bonusEarned };
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async claimReferral(code: string): Promise<void> {
      try {
        // Body is the generated `ClaimReferralBody` (`{ code }`); the outcome codes
        // (invalid_code / self_referral / already_referred / not_eligible) ride on the thrown
        // CandidateApiError.code, which toCandidateError preserves from the ProblemDetails body.
        await candidateApi.post('/api/referrals/claim', { body: { code: code.trim() } });
      } catch (error) {
        throw toCandidateError(error);
      }
    },

    async requestAccountDeletion() {
      try {
        await candidateApi.delete('/api/account');
      } catch (error) {
        throw toCandidateError(error);
      }
    },
  };
}
